import React, { useEffect, useState, useRef } from "react";
import { Sparkles, Loader2, Play, Pause, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * BulkEnrichCard — admin runner for the campsite description enrichment.
 * Most sites in the catalogue have basic 1-line descriptions; this card
 * lets the admin auto-generate richer 2-3 paragraph descriptions for
 * every campsite in batches, with a live progress bar.
 *
 *   • Each tap of "Run a batch" processes 50 sites (~30s) with 5
 *     parallel Claude calls under the hood.
 *   • "Auto-loop" mode keeps firing batches until done so the admin
 *     can walk away. Stop button halts gracefully after the current
 *     batch finishes (never mid-Claude-call).
 *   • Progress bar reflects the live ratio of enriched / total.
 *   • Cost estimate shown so the admin knows the spend before kicking
 *     off (rough cost = remaining × $0.001).
 */
export default function BulkEnrichCard() {
  // Two modes share the same UI shell: descriptions (AI rich copy) and
  // facilities (AI-inferred amenities). Switching tabs preserves the
  // batch-size + auto-loop state separately per mode.
  const [mode, setMode] = useState("descriptions");
  const [stats, setStats] = useState(null);
  const [running, setRunning] = useState(false);
  const [autoLoop, setAutoLoop] = useState(false);
  const [batchSize, setBatchSize] = useState(50);
  const [lastBatch, setLastBatch] = useState(null);
  const cancelRef = useRef(false);

  const cfg = mode === "descriptions"
    ? {
        statsPath: "/admin/campsites/enrich-stats",
        runPath: "/admin/campsites/enrich-bulk",
        eyebrow: "AI description enrichment",
        title: "Enrich every campsite description",
        subtitle: "Auto-generates a warm, evocative 2-3 paragraph description for every site that doesn't have one. Cached forever — each site is enriched at most once.",
        doneLabel: "All campsites have rich descriptions ✨",
        doneKey: "enriched",
        leftLabel: "Total sites",
        midLabel: "Enriched",
      }
    : {
        statsPath: "/admin/campsites/facility-stats",
        runPath: "/admin/campsites/infer-facilities-bulk",
        eyebrow: "AI facility inference",
        title: "Fill in missing facilities",
        subtitle: "Adds high-confidence facilities (toilets, showers, electric, dog-friendly, hiking, etc.) to sites with empty lists. STRICT rules — only adds a facility if ≥85% likely. Conservative when in doubt.",
        doneLabel: "Every site has a facility list ✓",
        doneKey: "with_facilities",
        leftLabel: "Total sites",
        midLabel: "With facilities",
      };

  const loadStats = async () => {
    try {
      const { data } = await api.get(cfg.statsPath);
      setStats(data);
    } catch (e) {
      toast.error("Failed to load stats");
    }
  };

  useEffect(() => { setStats(null); loadStats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [mode]);

  const runBatch = async () => {
    setRunning(true);
    try {
      // iter99 — background=true fire-and-forget: backend returns 202
      // immediately with {queued: true}, the batch runs server-side, we
      // then poll /enrich-stats to watch progress. This dodges Cloudflare's
      // 30 s edge timeout that used to throw 504s on bigger batches.
      const { data } = await api.post(`${cfg.runPath}?limit=${batchSize}&concurrency=5&background=true`);
      if (data?.queued || data?.running) {
        // Background mode — poll stats until the batch finishes (or auto-loop
        // is stopped). Polling cadence matches batch size: bigger batches
        // are checked less often to save bandwidth.
        const pollInterval = Math.min(8000, 2000 + batchSize * 15);
        const startStats = stats || {};
        const startProcessed = startStats[cfg.doneKey] || 0;
        let lastStats = startStats;
        // Wait up to 3 min per batch (200 sites × 0.7 s) before giving up.
        const maxWaitMs = Math.min(180_000, batchSize * 700 + 30_000);
        const deadline = Date.now() + maxWaitMs;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, pollInterval));
          if (cancelRef.current) break;
          await loadStats();
          // We need the freshest stats — re-read directly from the closure-friendly fetch.
          try {
            const { data: fresh } = await api.get(cfg.statsPath);
            lastStats = fresh;
            const enriched = fresh?.[cfg.doneKey] || 0;
            // Batch is done when either:
            //   • The progress counter has caught up by at least `batchSize` items
            //   • OR there's nothing left to enrich at all
            if (enriched - startProcessed >= batchSize || fresh?.remaining === 0) {
              break;
            }
          } catch { /* ignore poll errors and retry next tick */ }
        }
        const lastBatchInfo = {
          processed: Math.max(0, (lastStats?.[cfg.doneKey] || 0) - startProcessed),
          enriched: Math.max(0, (lastStats?.[cfg.doneKey] || 0) - startProcessed),
          failed: 0,
          remaining: lastStats?.remaining ?? 0,
          done: (lastStats?.remaining ?? 0) === 0,
        };
        setLastBatch(lastBatchInfo);
        return lastBatchInfo;
      }
      // Legacy synchronous path (background=false / older backend): the
      // response itself carries the counts.
      setLastBatch(data);
      await loadStats();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
      setAutoLoop(false);
      return null;
    } finally {
      setRunning(false);
    }
  };

  // Auto-loop: keep running batches until done OR user stops.
  useEffect(() => {
    if (!autoLoop || running) return;
    let killed = false;
    cancelRef.current = false;
    (async () => {
      while (!killed && !cancelRef.current) {
        const data = await runBatch();
        if (!data || data.done || data.processed === 0) {
          setAutoLoop(false);
          if (data?.done) toast.success(cfg.doneLabel);
          break;
        }
        await new Promise((r) => setTimeout(r, 400));
      }
    })();
    return () => { killed = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoop]);

  const pct = stats?.pct ?? 0;
  const remaining = stats?.remaining ?? 0;
  const estCost = (remaining * 0.001).toFixed(2);
  const midVal = stats?.[cfg.doneKey] ?? 0;

  return (
    <div className="bg-white border hairline p-5 sm:p-6" data-testid="bulk-enrich-card">
      <div className="flex flex-wrap items-center gap-1 mb-4" role="tablist" data-testid="bulk-enrich-tabs">
        {[
          { id: "descriptions", label: "Descriptions" },
          { id: "facilities", label: "Facilities" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={mode === t.id}
            onClick={() => { if (!running && !autoLoop) setMode(t.id); }}
            disabled={running || autoLoop}
            className={`px-3 py-1.5 mono text-[11px] uppercase tracking-[0.15em] border-2 ${
              mode === t.id ? "bg-[#1c1917] text-white border-[#1c1917]"
                            : "bg-white text-[#1c1917] border-[#e7e5e4] hover:border-[#1c1917]"
            } disabled:opacity-50`}
            data-testid={`bulk-enrich-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="eyebrow mb-1">{cfg.eyebrow}</div>
          <h3 className="font-display text-xl sm:text-2xl tracking-tight">
            {cfg.title}
          </h3>
          <p className="text-sm text-[#525252] mt-1">{cfg.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={loadStats}
          disabled={running}
          className="mono text-[11px] uppercase tracking-[0.2em] text-[#1c1917] hover:underline disabled:opacity-50"
          data-testid="bulk-enrich-refresh"
        >
          Refresh
        </button>
      </div>

      {!stats ? (
        <div className="text-sm text-[#525252] inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4" data-testid="bulk-enrich-stats">
            <div className="bg-[#fafaf9] border hairline p-3">
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#525252] mb-1">{cfg.leftLabel}</div>
              <div className="font-display text-2xl tabular-nums">{stats.total.toLocaleString("en-GB")}</div>
            </div>
            <div className="bg-[#dcfce7] border hairline p-3">
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#14532d] mb-1">{cfg.midLabel}</div>
              <div className="font-display text-2xl tabular-nums text-[#14532d]">{midVal.toLocaleString("en-GB")}</div>
            </div>
            <div className="bg-[#fef3c7] border hairline p-3">
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#7c2d12] mb-1">Remaining</div>
              <div className="font-display text-2xl tabular-nums text-[#7c2d12]">{stats.remaining.toLocaleString("en-GB")}</div>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between text-xs mono mb-1">
              <span className="uppercase tracking-[0.2em] text-[#525252]">Progress</span>
              <span className="text-[#1c1917] tabular-nums">{pct}%</span>
            </div>
            <div className="h-2 bg-[#e7e5e4] overflow-hidden" data-testid="bulk-enrich-progress">
              <div
                className="h-full bg-[#16a34a] transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {stats.remaining === 0 ? (
            <div className="flex items-center gap-2 text-sm text-[#14532d] bg-[#dcfce7] px-3 py-2 border-2 border-[#16a34a]">
              <CheckCircle2 className="w-4 h-4" />
              {cfg.doneLabel}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <label className="text-xs mono uppercase tracking-[0.2em] text-[#525252]">
                  Batch size
                  <select
                    value={batchSize}
                    onChange={(e) => setBatchSize(Number(e.target.value))}
                    disabled={running}
                    className="ml-2 border hairline px-2 py-1 text-[12px] mono"
                    data-testid="bulk-enrich-batch-size"
                  >
                    {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <span className="text-xs text-[#888]">
                  est. cost to complete: ~£{(estCost * 0.79).toFixed(2)}
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                {!autoLoop ? (
                  <>
                    <button
                      type="button"
                      onClick={runBatch}
                      disabled={running}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1c1917] text-white text-sm font-bold disabled:opacity-50"
                      data-testid="bulk-enrich-run-batch"
                    >
                      {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[#fde047]" />}
                      Run a batch ({batchSize} sites)
                    </button>
                    <button
                      type="button"
                      onClick={() => setAutoLoop(true)}
                      disabled={running}
                      className="inline-flex items-center gap-1.5 px-4 py-2 border-2 border-[#1c1917] bg-[#fde047] text-[#1c1917] text-sm font-bold disabled:opacity-50"
                      data-testid="bulk-enrich-auto-loop"
                    >
                      <Play className="w-4 h-4" />
                      Auto-loop until done
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => { cancelRef.current = true; setAutoLoop(false); }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 border-2 border-[#7c2d12] bg-[#fef3c7] text-[#7c2d12] text-sm font-bold"
                    data-testid="bulk-enrich-stop"
                  >
                    <Pause className="w-4 h-4" />
                    Stop after current batch
                  </button>
                )}
              </div>

              {lastBatch ? (
                <div className="mt-3 mono text-[11px] text-[#525252]" data-testid="bulk-enrich-last-batch">
                  Last batch: {lastBatch.enriched ?? lastBatch.added ?? 0} processed · {lastBatch.failed ?? 0} failed · {lastBatch.remaining.toLocaleString("en-GB")} remaining
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}
